import { Phone, Copy } from "lucide-react";
import { useLongPress } from "../../hooks/useLongPress";
import { copyToClipboard } from "../../lib/leads";
import { toast } from "./Toast";

/* Phone tap target: short tap → opens the dialer via tel: URL; long
   press (≥500ms) → copies the number to clipboard with a toast.
   `mobile` is the raw 10-digit number; `display` is the formatted
   string to render. The component stops propagation so tapping it
   inside a clickable row doesn't also open the lead detail. */
export function PhoneTap({ mobile, display, whatsapp, masked, className = "" }) {
  /* NRI leads have no Indian mobile -- SMS to a foreign number is
     unreliable, so they register with a WhatsApp number only and skip OTP.
     Without this the tap built "tel:+91null".

     With a mobile, nothing changes: +91 prefixed, tel: link, same copy
     behaviour. Without one, tapping opens WhatsApp instead, which is how
     the sales team would actually reach them. The number is used as stored
     since an international one already carries its country code. */
  const isWa = !mobile && !!whatsapp;
  const dialled = isWa ? String(whatsapp).replace(/[^\d]/g, "") : "+91" + mobile;

  const bind = useLongPress({
    onShortTap: () => {
      if (masked) return;
      window.location.href = isWa
        ? "https://wa.me/" + dialled
        : "tel:" + dialled;
    },
    onLongPress: () => { if (!masked) { copyToClipboard(isWa ? whatsapp : dialled); toast("Phone number copied"); } },
  });
  const copy = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!masked) { copyToClipboard(isWa ? whatsapp : dialled); toast("Phone number copied"); }
  };
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap ${className}`} onClick={(e) => e.stopPropagation()}>
      <span
        role="button"
        tabIndex={0}
        {...bind}
        // whitespace-nowrap keeps the number on one line. Without it the
        // leads table (11 columns, w-full, no min-width) compresses these
        // cells and the browser breaks the number mid-digit, which is both
        // unreadable and inflates every row's height.
        className={`inline-flex items-center gap-1.5 tabular-nums select-none whitespace-nowrap ${!masked ? "cursor-pointer text-blue-700 hover:text-blue-900" : "text-slate-500"}`}
        title={!masked ? "Tap to call · long-press to copy" : ""}
      >
        <Phone className="w-3.5 h-3.5" />{display}
      </span>
      {!masked && (
        <button
          type="button"
          onClick={copy}
          title="Copy number"
          aria-label="Copy phone number"
          className="text-slate-400 hover:text-blue-700 transition-colors p-0.5"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      )}
    </span>
  );
}
